import { useCallback, useSyncExternalStore } from 'react';
import {
  getDoneSets,
  isPersistent,
  resetExercise,
  subscribe,
  toggleSet,
} from './setProgress';
import type { PlanEntry } from '../types/exercise';

interface ExerciseProgress {
  done: readonly number[];
  isComplete: boolean;
  toggle: (setNumber: number) => void;
  reset: () => void;
  isPersistent: boolean;
}

export function useExerciseProgress(
  dayKey: string,
  exerciseId: string,
  totalSets: number,
): ExerciseProgress {
  const sub = useCallback((cb: () => void) => subscribe(cb), []);
  const getSnap = useCallback(
    () => getDoneSets(dayKey, exerciseId),
    [dayKey, exerciseId],
  );
  const done = useSyncExternalStore(sub, getSnap, getSnap);

  const toggle = useCallback(
    (n: number) => toggleSet(dayKey, exerciseId, n),
    [dayKey, exerciseId],
  );
  const reset = useCallback(
    () => resetExercise(dayKey, exerciseId),
    [dayKey, exerciseId],
  );

  return {
    done,
    isComplete: done.length >= totalSets,
    toggle,
    reset,
    isPersistent: isPersistent(),
  };
}

/**
 * Anzahl abgeschlossener Uebungen am `dayKey` aus den gegebenen Plan-Eintraegen.
 * Nutzt useSyncExternalStore, damit Aenderungen im Detail-Sheet sofort in der TodayView erscheinen.
 */
export function useDayCompletedCount(
  dayKey: string,
  entries: readonly PlanEntry[],
): number {
  const sub = useCallback((cb: () => void) => subscribe(cb), []);
  // Stabile Dependency-Signatur: id+target-sets kombiniert.
  const sig = entries.map((e) => `${e.exerciseId}:${e.sets}`).join('|');
  const getSnap = useCallback(() => {
    let completed = 0;
    for (const e of entries) {
      const done = getDoneSets(dayKey, e.exerciseId);
      if (done.length >= e.sets) completed += 1;
    }
    return completed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dayKey, sig]);
  return useSyncExternalStore(sub, getSnap, getSnap);
}
